/** Die Beispielwerte der Servervorschau, in beiden Datumsformaten des Renderers. */
export function sampleValues(objectName?: string) {
  return [
    'Max',
    'Apartment 02',
    objectName,
    '18.09.2026',
    '01.10.2026',
    '09/18/2026',
    '10/01/2026',
    'https://example.invalid/guest',
  ].filter((value): value is string => !!value?.trim());
}

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

function pattern(values: string[]) {
  const alternatives = [...values].sort((a, b) => b.length - a.length).map(escape);
  // Nur ganze Wörter: „Max" in „Maximal" ist kein eingesetzter Wert.
  return new RegExp(`(?<![\\p{L}\\p{N}])(${alternatives.join('|')})(?![\\p{L}\\p{N}])`, 'gu');
}

export interface Piece {
  text: string;
  value: boolean;
}

/** Zerlegt Text so, dass eingesetzte Werte markiert werden können — woher sie kommen, sieht man. */
export function markValues(text: string, values: string[]): Piece[] {
  if (values.length === 0) return [{ text, value: false }];
  const pieces: Piece[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern(values))) {
    if (match.index > last) pieces.push({ text: text.slice(last, match.index), value: false });
    pieces.push({ text: match[0], value: true });
    last = match.index + match[0].length;
  }
  if (last < text.length) pieces.push({ text: text.slice(last), value: false });
  return pieces;
}

const encode = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Der Renderer kodiert auch Umlaute und Apostrophe (`&#252;`, `&#39;`) — gesucht wird im Klartext. */
const decode = (value: string) =>
  value.replace(/&(?:#(\d+)|#x([\da-f]+)|(amp|lt|gt|quot|apos));/gi, (entity, dec, hex, name) => {
    if (dec) return String.fromCodePoint(Number(dec));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    return NAMED[String(name).toLowerCase()] ?? entity;
  });

/**
 * Dasselbe im Server-HTML: nur zwischen Tags wird markiert, nie in Attributen. Der Text dazwischen
 * wird dafür gelesen und danach wieder kodiert.
 */
export function markHtml(html: string, values: string[]) {
  if (values.length === 0) return html;
  return html
    .split(/(<[^>]*>)/)
    .map((chunk) =>
      chunk.startsWith('<')
        ? chunk
        : markValues(decode(chunk), values)
            .map((piece) =>
              piece.value ? `<mark>${encode(piece.text)}</mark>` : encode(piece.text),
            )
            .join(''),
    )
    .join('');
}

/**
 * Die Vorschau fügt nur die Markierung der Werte hinzu. Schrift, Button und Farben bringt die Mail
 * selbst mit – ein eigenes Vorschau-CSS ließe sie schöner aussehen als im Postfach.
 */
const PREVIEW_STYLE =
  '<style>mark{background:#e2e8dc;color:inherit;border-radius:3px;padding:0 2px}</style>';

/** Die Server-Mail mit markierten Werten; der Stil kommt in den Kopf, damit das Dokument gültig bleibt. */
export function previewDocument(html: string, values: string[]) {
  const marked = markHtml(html, values);
  // Vor dem Doctype schaltete der Stil den Browser in den Quirks-Modus – dann sähe die Vorschau
  // anders aus als das Postfach.
  const head = /<head\b[^>]*>/i;
  return head.test(marked)
    ? marked.replace(head, (tag) => tag + PREVIEW_STYLE)
    : PREVIEW_STYLE + marked;
}
